import { Controller } from '@hotwired/stimulus'
import dayjs from 'dayjs'
import flatpickr from 'flatpickr'
import type { Instance } from 'flatpickr/dist/types/instance'
import { broadcastInputChange } from 'packs/ui/app/components/ui/_input/_broadcast_change_utils'

type InputEventCustom = InputEvent & { target: HTMLInputElement }

const FOCUS_CLASSES: readonly string[] = ['border-viaorange']

export default class extends Controller<HTMLDivElement> {
  static targets = ['input', 'hidden', 'clear']

  declare readonly hasHiddenTarget: boolean
  declare readonly hiddenTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly clearTarget: HTMLButtonElement
  declare readonly hasClearTarget: boolean

  static values = {
    format: String,
    defaultValue: String,
    minDateAllowed: String,
    maxDateAllowed: String,
    isDisabled: Boolean
  }

  declare formatValue: string
  declare readonly hasFormatValue: boolean
  declare defaultValueValue: string
  declare readonly hasDefaultValueValue: boolean
  declare minDateAllowedValue: string
  declare readonly hasMinDateAllowedValue: boolean
  declare maxDateAllowedValue: string
  declare readonly hasMaxDateAllowedValue: boolean
  declare isDisabledValue: boolean
  declare readonly hasIsDisabledValue: boolean

  flatpickr: null | Instance = null
  formatToUse: string = 'YYYY-MM-DD'

  connect(): void {
    this.formatToUse = this.formatValue === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'
    this.#populateInputFromDefault()
    if (!this.isDisabledValue) this.#initFlatpickr()
    this.#populateHiddenFromInput()
  }

  #populateInputFromDefault(): void {
    if (!this.hasDefaultValueValue || this.defaultValueValue === '') return
    const [start, end] = this.defaultValueValue.split(';')

    const startPresent = typeof start === 'string' && start !== ''
    const endPresent = typeof end === 'string' && end !== ''

    if (startPresent && endPresent) {
      this.inputTarget.value = `${start} to ${end}`
    } else if (startPresent) {
      this.inputTarget.value = start
    } else {
      this.inputTarget.value = ''
    }
  }

  #populateHiddenFromInput(): void {
    // Already handled by flatpickr on change, but keep for completeness
    // No-op here
  }

  #initFlatpickr(): void {
    this.flatpickr = flatpickr(this.inputTarget, {
      mode: 'range',
      time_24hr: true,
      allowInput: false,
      defaultHour: 0,
      inline: false,
      disableMobile: true,
      maxDate: this.maxDateAllowedValue,
      minDate: this.minDateAllowedValue,
      minuteIncrement: 1,
      onChange: d => {
        this.#pickDateRange(d)
      }
    })
  }

  #pickDateRange(selectedDates: Date[]): void {
    if (!Array.isArray(selectedDates) || selectedDates.length === 0) {
      this.inputTarget.value = ''
      if (this.hasHiddenTarget && this.hiddenTarget != null) {
        this.hiddenTarget.value = ''
        broadcastInputChange(this.hiddenTarget)
      }
      return
    }

    const [start, end] = selectedDates

    if (start instanceof Date && end instanceof Date) {
      const startStr = dayjs(start).format(this.formatToUse)
      const endStr = dayjs(end).format(this.formatToUse)

      this.inputTarget.value = `${startStr} to ${endStr}`

      if (this.hasHiddenTarget && this.hiddenTarget != null) {
        this.hiddenTarget.value = `${startStr};${endStr}`
      }
    } else if (start instanceof Date) {
      const startStr = dayjs(start).format(this.formatToUse)

      this.inputTarget.value = startStr

      if (this.hasHiddenTarget && this.hiddenTarget != null) {
        this.hiddenTarget.value = startStr
      }
    }

    if (this.hasHiddenTarget && this.hiddenTarget != null) broadcastInputChange(this.hiddenTarget)
  }

  focus(): void {
    if (this.inputTarget != null) {
      this.inputTarget.classList.add(...FOCUS_CLASSES)
    }
  }

  blur(): void {
    if (this.inputTarget != null) {
      this.inputTarget.classList.remove(...FOCUS_CLASSES)
    }
  }

  input(e: InputEventCustom): void {
    // Prevent manual typing
    if ('target' in e && e.target instanceof HTMLInputElement) {
      ;(e.target as HTMLInputElement).value = this.inputTarget.value
    }
  }

  clickClear(): void {
    this.inputTarget.value = ''
    if (this.hasHiddenTarget && this.hiddenTarget != null) {
      this.hiddenTarget.value = ''
      broadcastInputChange(this.hiddenTarget)
    }
  }
}
